import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type {
  ProductSourceUploadEligibility,
  ProductSourceUploadIneligibilityReason,
} from '@/features/product-catalog/lib/assessProductSourceUploadEligibility'
import { formatPackagingDocumentFileSize } from '@/shared/components/packaging/packagingDocumentUploadPreview'
import { MAX_UPLOAD_FILE_SIZE_MB } from '@/shared/lib/uploadGuardrails'

type ProductSourceUploadReviewPanelProps = {
  assessments: ProductSourceUploadEligibility[]
  isAssessing: boolean
  isUploading: boolean
  hideActions?: boolean
  onChooseDifferentFiles: () => void
  onConfirmUpload: () => void
}

function fileRowKey(row: ProductSourceUploadEligibility, index: number): string {
  return `${row.file.name}-${String(row.file.lastModified)}-${String(index)}`
}

function statusLabel(
  translate: TFunction<'productCatalog'>,
  reason: ProductSourceUploadEligibility['reason']
): string {
  if (reason == null) {
    return translate('sources.addDialog.review.status.eligible')
  }
  if (reason === 'unsupported-type') {
    return translate('sources.addDialog.review.status.unsupportedType')
  }
  if (reason === 'too-large') {
    return translate('sources.addDialog.review.status.tooLarge')
  }
  if (reason === 'encrypted') {
    return translate('sources.addDialog.review.status.encrypted')
  }
  if (reason === 'invalid-pdf') {
    return translate('sources.addDialog.review.status.invalidPdf')
  }
  return translate('sources.addDialog.review.status.rightsProtected')
}

function statusTooltip(
  translate: TFunction<'productCatalog'>,
  reason: ProductSourceUploadIneligibilityReason
): string {
  const uploadLimitHint = { maxSizeMb: MAX_UPLOAD_FILE_SIZE_MB }
  if (reason === 'unsupported-type') {
    return translate('sources.addDialog.review.status.tooltip.unsupportedType')
  }
  if (reason === 'too-large') {
    return translate('sources.addDialog.review.status.tooltip.tooLarge', uploadLimitHint)
  }
  if (reason === 'encrypted') {
    return translate('sources.addDialog.review.status.tooltip.encrypted')
  }
  if (reason === 'invalid-pdf') {
    return translate('sources.addDialog.review.status.tooltip.invalidPdf')
  }
  return translate('sources.addDialog.review.status.tooltip.rightsProtected')
}

function ReviewFileList({
  rows,
  variant,
  getStatusLabel,
  getStatusTooltip,
}: {
  rows: ProductSourceUploadEligibility[]
  variant: 'eligible' | 'ineligible'
  getStatusLabel: (reason: ProductSourceUploadEligibility['reason']) => string
  getStatusTooltip?: (reason: ProductSourceUploadIneligibilityReason) => string
}) {
  const showIneligibleStatus = variant === 'ineligible'

  return (
    <ul className='max-h-48 min-w-0 space-y-3 overflow-y-auto overflow-x-hidden'>
      {rows.map((row, index) => (
          <li
            key={fileRowKey(row, index)}
            className='min-w-0 space-y-1.5 text-sm'
          >
            <p className='break-all font-medium' title={row.file.name}>
              {row.file.name}
            </p>
            <div className='flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1'>
              <p className='text-xs text-muted-foreground tabular-nums'>
                {formatPackagingDocumentFileSize(row.file.size)}
              </p>
              {showIneligibleStatus &&
                row.reason != null &&
                getStatusTooltip != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex cursor-help'>
                      <Badge
                        variant='destructive'
                        className='max-w-full whitespace-normal text-left'
                      >
                        {getStatusLabel(row.reason)}
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side='top' className='max-w-sm'>
                    {getStatusTooltip(row.reason)}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </li>
        ))}
    </ul>
  )
}

export function ProductSourceUploadReviewPanel({
  assessments,
  isAssessing,
  isUploading,
  hideActions = false,
  onChooseDifferentFiles,
  onConfirmUpload,
}: ProductSourceUploadReviewPanelProps) {
  const { t } = useTranslation('productCatalog')

  const eligibleAssessments = assessments.filter((row) => row.eligible)
  const ineligibleAssessments = assessments.filter((row) => !row.eligible)
  const eligibleCount = eligibleAssessments.length
  const ineligibleCount = ineligibleAssessments.length
  const totalCount = assessments.length
  const isBusy = isAssessing || isUploading

  const getStatusLabel = (reason: ProductSourceUploadEligibility['reason']) =>
    statusLabel(t, reason)

  const getStatusTooltip = (reason: ProductSourceUploadIneligibilityReason) =>
    statusTooltip(t, reason)

  const showReviewSections = !isAssessing && totalCount > 0

  return (
    <div className='space-y-4'>
      {isAssessing && (
        <p className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='size-4 shrink-0 animate-spin' aria-hidden />
          {t('sources.addDialog.review.assessing')}
        </p>
      )}

      {!isAssessing && totalCount > 0 && (
        <p className='text-sm text-muted-foreground'>
          {t('sources.addDialog.review.summary', {
            eligible: eligibleCount,
            total: totalCount,
          })}
        </p>
      )}

      {showReviewSections && eligibleCount > 0 && (
        <section className='space-y-2 rounded-md border border-border p-3'>
          <h3 className='text-sm font-medium'>
            {t('sources.addDialog.review.eligibleSectionTitle', {
              count: eligibleCount,
            })}
          </h3>
          <ReviewFileList
            rows={eligibleAssessments}
            variant='eligible'
            getStatusLabel={getStatusLabel}
          />
        </section>
      )}

      {showReviewSections && ineligibleCount > 0 && (
        <section className='space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3'>
          <div className='space-y-1'>
            <h3 className='text-sm font-medium text-destructive'>
              {t('sources.addDialog.review.ineligibleSectionTitle', {
                count: ineligibleCount,
              })}
            </h3>
            <p className='text-sm text-muted-foreground'>
              {t('sources.addDialog.review.ineligibleSectionDescription')}
            </p>
          </div>
          <ReviewFileList
            rows={ineligibleAssessments}
            variant='ineligible'
            getStatusLabel={getStatusLabel}
            getStatusTooltip={getStatusTooltip}
          />
        </section>
      )}

      {!hideActions && (
        <div className='flex flex-wrap justify-end gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={isBusy}
            onClick={onChooseDifferentFiles}
          >
            {t('sources.addDialog.review.chooseDifferentFiles')}
          </Button>
          <Button
            type='button'
            size='sm'
            disabled={isBusy || eligibleCount === 0}
            onClick={onConfirmUpload}
          >
            {isUploading ? (
              <>
                <Loader2 className='me-2 size-4 animate-spin' aria-hidden />
                {t('sources.upload.uploading')}
              </>
            ) : (
              t('sources.addDialog.review.uploadEligible', { count: eligibleCount })
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
